<template>
    <div class="flex flex-col h-full items-center justify-center lg:px-0 lg:py-0 px-4 py-10">
        <h4 class="text-center m-y-md md:w-1/2 w-full">{{ $t('sign_up.find_organization_heading') }}</h4>
        <p class="md:w-2/3 text-center w-full">{{ $t('sign_up.find_organization_info_long') }}</p>
        <form-progress-bar :steps="3" :current-step="2"></form-progress-bar>

        <form class="md:w-1/2 pb-6 w-full" @submit.prevent="onSubmit">
            <div class="flex">
                <div class="flex-grow input-with-icon mr-3">
                    <input class="form-control" id="search_query" name="search_query" type="text" :placeholder="$t('sign_up.organization_name')" required autocomplete="off" v-model="searchQuery" />
                    <span class="icon icon-search input-icon"></span>
                </div>
                <button class="btn btn-primary" type="submit">{{ $t('globals.search') }}</button>
            </div>
        </form>

        <div v-if="searchIsSuccessful && displayedOrganizations.length === 0" class="flex flex-col items-center justify-center w-full">
            <div class="md:w-1/4 pb-8 w-1/2">
                <svg-file-searching></svg-file-searching>
            </div>
            <div class="md:w-1/2 text-center w-full">
                <h5>{{ $t('globals.no_results_found') }}</h5>
                <p>{{ $t('globals.no_results_found_tip') }}</p>
            </div>
        </div>

        <div v-else class="md:w-2/3 w-full">
            <ul>
                <li class="border-b border-gray-400 flex items-center py-3 w-full" :class="{ 'border-t': index === 0 }" v-for="(organization, index) in displayedOrganizations">
                    <div class="bg-primary-500 flex h-8 items-center justify-center mr-4 p-2 rounded w-8">
                        <svg class="block fill-current h-8 text-white w-8" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M7 8a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm0 1c2.15 0 4.2.4 6.1 1.09L12 16h-1.25L10 20H4l-.75-4H2L.9 10.09A17.93 17.93 0 0 1 7 9zm8.31.17c1.32.18 2.59.48 3.8.92L18 16h-1.25L16 20h-3.96l.37-2h1.25l1.65-8.83zM13 0a4 4 0 1 1-1.33 7.76 5.96 5.96 0 0 0 0-7.52C12.1.1 12.53 0 13 0z"/></svg>
                    </div>
                    <div class="flex flex-col flex-grow text-left">
                        <div class="font-bold text-sm">{{ organization.name }}</div>
                        <div v-html="$t('sign_up.owned_by', { owner: `<span class=font-medium>${organization.owner.first_name} ${organization.owner.last_name}</span>` })"></div>
                    </div>
                    <div class="pr-1">
                        <button @click="apply(organization.id)" class="btn btn-outline-primary btn-sm">{{ $t('sign_up.apply') }}</button>
                    </div>
                </li>
            </ul>
            <ul v-if="totalPages && totalPages > 1" class="flex justify-center pagination">
                <li :class="{ 'disabled': currentPage === 1 }">
                    <a @click="currentPage !== 1 && currentPage--" href="javascript:;">&laquo;</a>
                </li>
                <li :class="{ 'active': page.active, 'disabled': page.disabled }" v-for="page in displayedPages">
                    <a v-if="page.disabled" href="javascript:;">...</a>
                    <a v-else @click="currentPage = page.label" href="javascript:;">{{ page.label }}</a>
                </li>
                <li :class="{ 'disabled': currentPage === totalPages }">
                    <a @click="currentPage !== totalPages && currentPage++" href="javascript:;">&raquo;</a>
                </li>
            </ul>
        </div>

        <div class="md:w-2/3 pt-4 w-full">
            <a @click="goBack" class="font-medium link-muted no-underline text-left" href="javascript:;">&larr; {{ $t('globals.back') }}</a>
        </div>
    </div>
</template>

<script>
import FormProgressBar from './form-progress-bar.vue';
import SvgFileSearching from '../../../svg/file-searching.vue';

export default {
    components: {
        'form-progress-bar': FormProgressBar,
        'svg-file-searching': SvgFileSearching,
    },

    data() {
        return {
            organizations: [],
            searchQuery: '',
            searchIsSuccessful: false,
            currentPage: 1,
            perPage: 3,
            pages: [],
        };
    },

    computed: {
        displayedOrganizations() {
            let from = (this.currentPage * this.perPage) - this.perPage;
            let to = (this.currentPage * this.perPage);
            return this.organizations.slice(from, to);
        },

        totalPages() {
            return [...this.pages].pop();
        },

        displayedPages() {
            const previousPage = this.currentPage > 1 ? (this.currentPage - 1) : 1;
            const nextPage = this.currentPage < this.totalPages ? (this.currentPage + 1) : this.totalPages;
            const secondPreviousPage = previousPage === 3 ? 2 : null;
            const secondNextPage = nextPage === (this.totalPages - 2) ? (this.totalPages - 1) : null;
            const dotsBefore = previousPage > 3 ? 2 : null;
            const dotsAfter = nextPage < (this.totalPages - 2) ? (this.totalPages - 1) : null;

            let displayedPages = [];
            for (let page = 1; page <= this.totalPages; page++) {
                if ([1, this.totalPages, this.currentPage, previousPage, nextPage, secondPreviousPage, secondNextPage, dotsBefore, dotsAfter].includes(page)) {
                    displayedPages.push({
                        label: page,
                        active: this.currentPage === page,
                        disabled: [dotsBefore, dotsAfter].includes(page)
                    });
                }
            }
            return displayedPages;
        },
    },

    watch: {
        organizations() {
            this.currentPage = 1;
            this.setPages();
        },
    },

    methods: {
        onSubmit() {
            axios.get(`/api/public/organizations?filter[name]=${this.searchQuery}`)
                .then(response => {
                    this.organizations = response.data;
                    this.searchIsSuccessful = true;
                })
                .catch(() => {
                    this.organizations = [];
                    this.searchIsSuccessful = false;
                    window.toastr.clear();
                    window.toastr.error(this.$t('globals.unexpected_error'));
                });
        },

        setPages() {
            this.pages = [];
            const numberOfPages = Math.ceil(this.organizations.length / this.perPage);
            for (let index = 1; index <= numberOfPages; index++) {
                this.pages.push(index);
            }
        },

        goBack() {
            Bus.$emit('changeScreen', { hash: '', step: 1 });
        },

        apply(organizationId) {
            Bus.$emit('changeScreen', {
                hash: 'find',
                step: 3,
                organizationId: organizationId
            });
        },
    },
};
</script>
